import { computed, onMounted, type ComputedRef } from 'vue'
import useSearchParams from './useSearchParams'
import type { Tab } from '../../../Types/tab'

interface UseTabOptions<T extends Tab> {
  tabs: ComputedRef<T[]>
  modelValue: ComputedRef<T | null>
  onTabChange: (tab: T) => void
  queryParamName?: string
}

export default function useTab<T extends Tab>({
  tabs,
  modelValue,
  onTabChange,
  queryParamName = 'tab',
}: UseTabOptions<T>) {
  const tabsShown = computed<T[]>(() => {
    return tabs.value.filter((tab) => tab.show)
  })

  function setQueryParam(key: string): void {
    if (typeof window === 'undefined') {
      return
    }

    const url = new URL(window.location.href)
    url.searchParams.set(queryParamName, key)
    window.history.replaceState({}, '', url)
  }

  function setActiveTab(tab: T): void {
    if (tab.key === modelValue.value?.key) {
      return
    }

    onTabChange(tab)
    setQueryParam(tab.key)
  }

  function syncTabFromQuery(): void {
    if (tabsShown.value.length === 0 || typeof window === 'undefined') {
      return
    }

    const { searchParams } = useSearchParams()
    const tabParam = searchParams.get(queryParamName)
    const tabFromQuery = tabsShown.value.find((tab) => tab.key === tabParam)

    if (tabFromQuery) {
      if (tabFromQuery.key !== modelValue.value?.key) {
        onTabChange(tabFromQuery)
      }
      return
    }

    if (!modelValue.value) {
      const firstTab = tabsShown.value[0]
      if (!firstTab) {
        return
      }

      onTabChange(firstTab)
      setQueryParam(firstTab.key)
    }
  }

  onMounted(() => {
    syncTabFromQuery()
  })

  return {
    tabsShown,
    setActiveTab,
  }
}
